<script setup>
import AmountSummary from "./components/AmountSummary.vue";
import HeroSection from "./components/HeroSection.vue";
import PaymentActionPanel from "./components/PaymentActionPanel.vue";
import KrwBankDetailsPanel from "./components/KrwBankDetailsPanel.vue";
import PaymentOptionsPanel from "./components/PaymentOptionsPanel.vue";
import QrPanel from "./components/QrPanel.vue";
import StatusOverlay from "./components/StatusOverlay.vue";
import { useLocale } from "./composables/useLocale";
import { usePaymentPage } from "./composables/usePaymentPage";

const { currentLang, fieldTranslations, langLabel, setLanguage, supportedLangs, t } =
  useLocale();

const {
  actionButtonLabel,
  actionCooldownRemaining,
  actionError,
  canceledMessage,
  dismissError,
  displayAmount,
  errorMessage,
  getPaymentOptionClass,
  handleActionButton,
  handlePaymentOptionSelected,
  handleQrAttempt,
  handleReturn,
  hasUtrField,
  isInrCurrency,
  isLoading,
  paidMessage,
  paymentButtonsDisabled,
  paymentOptions,
  popupState,
  processedMessage,
  qrCopyLabel,
  qrCopyValue,
  krwBankDetailRows,
  krwBankPanelCaption,
  krwBankPanelHint,
  qrLoaded,
  qrPanelCaption,
  qrPanelHint,
  qrUrl,
  setQrLoaded,
  showHeaderContent,
  showPaymentButtonsSection,
  showKrwBankDetailsSection,
  showQrSection,
  timerValue,
  timerVisible,
  timerWarning,
  utrInput,
} = usePaymentPage({
  fieldTranslations,
  t,
});

function updateUtrInput(value) {
  utrInput.value = value;
}
</script>

<template>
  <div class="decor" aria-hidden="true">
    <span class="b1"></span>
    <span class="b2"></span>
    <span class="b3"></span>
  </div>

  <div class="shell">
    <div
      class="desktop-grid"
      :class="{ 'header-content-hidden': !showHeaderContent }"
    >
      <HeroSection
        :has-utr-field="hasUtrField"
        :supported-langs="supportedLangs"
        :current-lang="currentLang"
        :lang-label="langLabel"
        :t="t"
        @select-language="setLanguage"
      />

      <AmountSummary
        :display-amount="displayAmount"
        :is-inr-currency="isInrCurrency"
        :is-loading="isLoading"
        :t="t"
        :timer-value="timerValue"
        :timer-visible="timerVisible"
        :timer-warning="timerWarning"
      />

      <div class="desktop-left">
        <div v-if="isLoading" class="panel state-panel">
          <div class="spinner"></div>
          <p class="state-text">{{ t("loadingOrder") }}</p>
        </div>

        <div v-else-if="errorMessage" class="panel error-panel">
          <strong>{{ t("error") }}</strong>
          <span>{{ errorMessage }}</span>
        </div>

        <template v-else>


          <KrwBankDetailsPanel
            v-if="showKrwBankDetailsSection"
            :caption="krwBankPanelCaption"
            :hint="krwBankPanelHint"
            :rows="krwBankDetailRows"
            :t="t"
          />

          <PaymentOptionsPanel
            v-if="showPaymentButtonsSection"
            :payment-buttons-disabled="paymentButtonsDisabled"
            :payment-option-class="getPaymentOptionClass"
            :payment-options="paymentOptions"
            :t="t"
            @select="handlePaymentOptionSelected"
          />
          <QrPanel
              v-if="showQrSection"
              :qr-loaded="qrLoaded"
              :qr-url="qrUrl"
              :qr-caption-text="qrPanelCaption"
              :qr-hint-text="qrPanelHint"
              :copy-row-label="qrCopyLabel"
              :copy-row-value="qrCopyValue"
              :t="t"
              @load="setQrLoaded(true)"
              @attempt="handleQrAttempt"
          />
        </template>
      </div>

      <div class="desktop-right">
        <PaymentActionPanel
          v-if="!isLoading && !errorMessage"
          :action-button-label="actionButtonLabel"
          :has-utr-field="hasUtrField"
          :popup-state="popupState"
          :t="t"
          :utr-input="utrInput"
          :cooldown-remaining="actionCooldownRemaining"
          :action-error="actionError"
          @action="handleActionButton"
          @update:utr-input="updateUtrInput"
        />
      </div>
    </div>
  </div>

  <StatusOverlay
    :has-utr-field="hasUtrField"
    :supported-langs="supportedLangs"
    :current-lang="currentLang"
    :lang-label="langLabel"
    :popup-state="popupState"
    :processed-message="processedMessage"
    :paid-message="paidMessage"
    :canceled-message="canceledMessage"
    :t="t"
    @return="handleReturn"
    @retry="dismissError"
    @select-language="setLanguage"
  />
</template>
